/**
 * ICT: готовые связки школы Майкла Хаддлстона (Inner Circle Trader).
 *
 * Каждая связка — последовательность: сначала одно событие, потом другое,
 * и вход — на третьем. Между ними время, поэтому ранние шаги стоят в фоне
 * с окном «за последние N баров».
 */
import { above, below, crossDown, crossUp, happened, ind, inZone, mirror, number, premium, recently, rvol, spec, structureTrend, swingParams, weight, } from './kit.js';
export const ICT_TEMPLATES = [
    {
        id: 'ict',
        name: 'ICT Reversal',
        icon: '🏛',
        category: 'ict',
        popular: true,
        hint: 'Снятие ликвидности, слом характера и вход на возврате в разрыв цены.',
        steps: ['Снятие ликвидности', 'Слом структуры', 'Возврат в FVG', 'Дискаунт и объём'],
        keywords: ['ict', 'разворот', 'ликвидность', 'mss', 'fvg'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Классическая модель разворота ICT: стопы собраны, характер рынка сменился, и цена вернулась в оставленный разрыв.',
            why: 'Три шага подряд — редкое совпадение, и когда оно случается, это чаще настоящий поворот, чем шум.',
            fails: 'На вялом рынке без объёма шаги складываются случайно, и модель не срабатывает.',
            tip: 'Модель ценят за соотношение риска: защита за снятым экстремумом близко, цель — у противоположной ликвидности.',
        },
        build: (o) => spec(o, 'ICT Reversal', mirror((side) => ({
            context: [recently(ind('sweep', swingParams, side.liquidity), 20), recently(ind('choch', swingParams, side.way), 10)],
            trigger: happened(ind('fvg', { side: side.sign, size: 0.25 }, 'touched')),
            confirmations: [
                weight('c1', inZone(side, 50), 40),
                weight('c2', above(rvol, number(1.2)), 30),
                weight('c3', side.beyond(structureTrend(), number(0)), 30),
            ],
        })), 50),
    },
    {
        id: 'ict-continuation',
        name: 'ICT Continuation',
        icon: '🏛',
        category: 'ict',
        hint: 'Структура уже наша, цена откатилась в ордер-блок — вход по ходу движения.',
        steps: ['Структура в нашу сторону', 'Пробой структуры', 'Возврат в ордер-блок', 'Объём'],
        keywords: ['ict', 'продолжение', 'bos', 'order block'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Тренд подтверждён пробоем структуры, цена откатилась в ордер-блок, откуда начался последний импульс.',
            why: 'Откат в ордер-блок — дешёвая точка входа внутри уже работающего тренда.',
            fails: 'Поздний ордер-блок в конце тренда часто не держит: сил на новый импульс уже нет.',
            tip: 'Берите ордер-блоки первых двух пробоев структуры — потом тренд обычно устаёт.',
        },
        build: (o) => spec(o, 'ICT Continuation', mirror((side) => ({
            context: [side.beyond(structureTrend(), number(0)), recently(ind('bos', swingParams, side.way), 20)],
            trigger: happened(ind('order_block', { side: side.sign, strength: 1.5 }, 'touched')),
            confirmations: [weight('c1', inZone(side, 60), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 50),
    },
    {
        id: 'ict-liquidity-raid',
        name: 'ICT Liquidity Raid',
        icon: '🏛',
        category: 'ict',
        hint: 'Ликвидность забрали, затем сильное движение обратно — вход в оставленный разрыв.',
        steps: ['Ликвидность забрали', 'Сильное движение обратно — пробой структуры', 'Возврат в разрыв', 'Объём'],
        keywords: ['ict', 'raid', 'ликвидность', 'displacement'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: '«Налёт» на ликвидность: стопы собраны, и цена сразу уходит обратно так резко, что пробивает структуру и оставляет разрыв.',
            why: 'Резкость ухода (displacement) показывает, что за разворотом стоят крупные деньги.',
            fails: 'Без резкого ухода это просто колебание вокруг экстремума.',
            tip: 'Чем крупнее разрыв после налёта, тем сильнее намерение.',
        },
        build: (o) => spec(o, 'ICT Liquidity Raid', mirror((side) => ({
            context: [recently(ind('sweep', swingParams, side.liquidity), 20), recently(ind('bos', swingParams, side.way), 15)],
            trigger: happened(ind('fvg', { side: side.sign, size: 0.25 }, 'touched')),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', inZone(side, 50), 40)],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'ote',
        name: 'OTE — оптимальный вход',
        icon: '🎯',
        category: 'ict',
        hint: 'После смены характера цена откатилась в «оптимальную зону» — 62–79 % от последнего движения.',
        steps: ['Недавно был слом характера в нашу сторону', 'Откат дошёл до зоны 62–79 %', 'Объём'],
        keywords: ['ote', 'optimal trade entry', 'фибоначчи', 'fibonacci', '0.62', '0.79', 'ict'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'ICT называет зону отката 62–79 % «оптимальным входом»: достаточно глубоко, чтобы купить дёшево, и не так глубоко, чтобы движение сломалось.',
            why: 'Глубокий откат после смены структуры собирает стопы ранних участников и даёт вход с коротким риском.',
            fails: 'Если откат уходит за 100 % — за начало движения — идея отменяется.',
            tip: 'Защита — за началом движения; цель — последний экстремум и дальше.',
        },
        build: (o) => spec(o, 'OTE — оптимальный вход', mirror((side) => ({
            context: [recently(ind('choch', swingParams, side.way), 40), side.sign > 0 ? above(premium(), number(21)) : below(premium(), number(79))],
            trigger: side.sign > 0 ? crossDown(premium(), number(38)) : crossUp(premium(), number(62)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 60), weight('c2', side.beyond(structureTrend(), number(0)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=ict.js.map